import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import http from "../../utils/http";
import { AxiosResponse } from "axios";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { AuthLayout, SignInWrapper } from "./auth.style";
import { StyledButton } from "../Common/styles/button";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import isEmail from "validator/es/lib/isEmail";
import { DarkLogo } from "../Common/assets/Sidebar";

const schema = yup.object({
  firstName: yup.string().required("*First name is required"),
  lastName: yup.string().required("*Last name is required"),
  email: yup
    .string()
    .required("*Email ID is Required")
    .test("is-valid", "*Please enter a valid Email ID.", (value) =>
      value ? isEmail(value) : false
    ),
  password: yup
    .string()
    .min(8, "*Password must be at least 8 characters")
    .required("*Password is Required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "*Passwords must match")
    .required("*Please confirm your password"),
  acceptTerms: yup
    .boolean()
    .oneOf([true], "*You must accept the terms and conditions"),
});

const AccountCreation = () => {
  const navigate = useNavigate();
  const [submitLoader, setSubmitLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const handleSubmit = async (data: any) => {
    try {
      setSubmitLoader(true);

      const conditions = ["crohn", "ulcerative"];

      const body = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        conditions: conditions,
      };

      const res: AxiosResponse = await http.post(
        "/participants/register",
        body
      );

      toastMessage(
        "success",
        "Account created successfully! Please check your email for verification."
      );

      // Navigate to email verification
      navigate(
        `/registry/verify-email?email=${data.email}&token=${res.data?.data}`
      );
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <Box sx={AuthLayout}>
      <Box sx={SignInWrapper}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
          <DarkLogo />
        </Box>

        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            confirmPassword: "",
            acceptTerms: false,
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ errors, touched, getFieldProps, values }) => {
            const passwordStrength = getPasswordStrength(values.password);
            const strengthLabels = [
              "Very Weak",
              "Weak",
              "Fair",
              "Good",
              "Strong",
            ];
            const strengthColors = [
              "#ef4444",
              "#f97316",
              "#eab308",
              "#3b82f6",
              "#22c55e",
            ];

            return (
              <Form>
                {/* Name Fields */}
                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel sx={LabelStyle} htmlFor="firstName">
                      First Name
                    </FormLabel>
                    <TextField
                      placeholder="Enter your first name"
                      id="firstName"
                      {...getFieldProps("firstName")}
                      error={
                        touched?.firstName && errors?.firstName ? true : false
                      }
                      helperText={
                        touched?.firstName && errors?.firstName
                          ? errors?.firstName
                          : " "
                      }
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel sx={LabelStyle} htmlFor="lastName">
                      Last Name
                    </FormLabel>
                    <TextField
                      placeholder="Enter your last name"
                      id="lastName"
                      {...getFieldProps("lastName")}
                      error={
                        touched?.lastName && errors?.lastName ? true : false
                      }
                      helperText={
                        touched?.lastName && errors?.lastName
                          ? errors?.lastName
                          : " "
                      }
                    />
                  </FormControl>
                </Box>

                {/* Email Field */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="email">
                    Email Address
                  </FormLabel>
                  <TextField
                    placeholder="Enter your email address"
                    id="email"
                    {...getFieldProps("email")}
                    error={touched?.email && errors?.email ? true : false}
                    helperText={
                      touched?.email && errors?.email ? errors?.email : " "
                    }
                  />
                </FormControl>

                {/* Password Field */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="password">
                    Password
                  </FormLabel>
                  <TextField
                    placeholder="Create a strong password"
                    id="password"
                    {...getFieldProps("password")}
                    error={touched?.password && errors?.password ? true : false}
                    helperText={
                      touched?.password && errors?.password
                        ? errors?.password
                        : " "
                    }
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {/* Password Strength Indicator */}
                  {values.password && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                        {[...Array(5)].map((_, i) => (
                          <Box
                            key={i}
                            sx={{
                              height: 4,
                              flex: 1,
                              borderRadius: 1,
                              backgroundColor:
                                i < passwordStrength
                                  ? strengthColors[passwordStrength - 1]
                                  : "#e2e8f0",
                              transition: "background-color 0.2s",
                            }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Password strength:{" "}
                        {strengthLabels[passwordStrength - 1] || "Very Weak"}
                      </Typography>
                    </Box>
                  )}
                </FormControl>

                {/* Confirm Password Field */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="confirmPassword">
                    Confirm Password
                  </FormLabel>
                  <TextField
                    placeholder="Confirm your password"
                    id="confirmPassword"
                    {...getFieldProps("confirmPassword")}
                    error={
                      touched?.confirmPassword && errors?.confirmPassword
                        ? true
                        : false
                    }
                    helperText={
                      touched?.confirmPassword && errors?.confirmPassword
                        ? errors?.confirmPassword
                        : " "
                    }
                    type={showConfirmPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                {/* Terms and Conditions */}
                <FormControl sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...getFieldProps("acceptTerms")}
                        checked={values.acceptTerms}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        I agree to the{" "}
                        <NavLink
                          to="/terms"
                          style={{ color: "#1d4ed8", textDecoration: "none" }}
                        >
                          Terms of Service
                        </NavLink>{" "}
                        and{" "}
                        <NavLink
                          to="/privacy"
                          style={{ color: "#1d4ed8", textDecoration: "none" }}
                        >
                          Privacy Policy
                        </NavLink>
                      </Typography>
                    }
                  />
                  {touched?.acceptTerms && errors?.acceptTerms && (
                    <Typography
                      variant="caption"
                      sx={{ color: "error.main", ml: 4 }}
                    >
                      {errors.acceptTerms}
                    </Typography>
                  )}
                </FormControl>

                {/* Submit Button */}
                {!submitLoader ? (
                  <StyledButton variant="contained" type="submit" fullWidth>
                    Create Account
                  </StyledButton>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={25} />
                  </Box>
                )}

                {/* Footer Links */}
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Already have an account?{" "}
                    <NavLink
                      to="/registry/login"
                      style={{ color: "#1d4ed8", textDecoration: "none" }}
                    >
                      Sign in here
                    </NavLink>
                  </Typography>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Box>
  );
};

export default AccountCreation;
